import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Spinner } from '@/shared/ui/spinner';
import type { AdminStore } from '@/shared/types/client';

interface ClientCloneStoresStepProps {
  stores: AdminStore[];
  onChange: (stores: AdminStore[]) => void;
  /** Índices de linhas já editadas manualmente pelo operador — usado só
   * pra decidir se a "licença" do step 1 pode sobrescrever `branchId` de
   * uma linha (não sobrescreve o que já foi editado à mão). */
  touchedIndexes: Set<number>;
  onTouchedChange: (indexes: Set<number>) => void;
  loading: boolean;
}

const EMPTY_STORE: AdminStore = {
  storeName: '',
  branchName: '',
  branchId: '',
  storeCod: '',
  storeAddress: '',
  storeType: '',
  storeLocal: '',
};

/**
 * 2º step da clonagem: lista editável das lojas do client de origem (busca
 * via `fetchPassbookStores`, feita no dialog pai). `branchId`/`storeCod`
 * são o mesmo "slot" livre que cada projeto usa como licença de PDV, CNPJ
 * ou código arbitrário — mostramos como um campo só ("Código"), já que os
 * dois sempre recebem o mesmo valor na prática.
 */
export function ClientCloneStoresStep({
  stores,
  onChange,
  touchedIndexes,
  onTouchedChange,
  loading,
}: ClientCloneStoresStepProps) {
  const updateStore = (index: number, patch: Partial<AdminStore>, markTouched = false) => {
    onChange(stores.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    if (markTouched) {
      const next = new Set(touchedIndexes);
      next.add(index);
      onTouchedChange(next);
    }
  };

  const removeStore = (index: number) => {
    onChange(stores.filter((_, i) => i !== index));
    const next = new Set<number>();
    touchedIndexes.forEach((i) => {
      if (i < index) next.add(i);
      else if (i > index) next.add(i - 1);
    });
    onTouchedChange(next);
  };

  const addStore = () => onChange([...stores, { ...EMPTY_STORE }]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Lojas do cliente de origem — revise, ajuste ou remova antes de confirmar. Pode terminar sem
        nenhuma loja.
      </p>

      {stores.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
          Nenhuma loja.
        </p>
      ) : (
        <div className="space-y-3">
          {stores.map((store, index) => (
            <div
              key={index}
              className="space-y-2 rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-3"
            >
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Nome da loja</Label>
                  <Input
                    value={store.storeName ?? ''}
                    onChange={(e) =>
                      updateStore(index, { storeName: e.target.value, branchName: e.target.value })
                    }
                    placeholder="Nome exibido"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Código (licença / CNPJ)</Label>
                  <Input
                    value={store.branchId ?? ''}
                    onChange={(e) =>
                      updateStore(index, { branchId: e.target.value, storeCod: e.target.value }, true)
                    }
                    placeholder="branchId / storeCod"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={() => removeStore(index)}>
                  <Trash2 className="size-4" /> Remover
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" size="sm" onClick={addStore}>
        <Plus className="size-4" /> Adicionar loja
      </Button>
    </div>
  );
}
